// ─────────────────────────────────────────────
//  Login.jsx  —  Login / Signup Page (UI Only)
//  All logic is in Auth.js
//  All class names prefixed with "login-"
// ─────────────────────────────────────────────

import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import "./Login.css";
import {
  loginUser,
  signupUser,
  verifyOtp,
  verifyEmailOtpAndLogin,
  verifyPhoneForSignup,
  forgotPasswordOtp,
  resetPassword,
  resetPasswordByPhone,
  googleSignIn,
  completeGoogleSignup,
  sendPhoneOtpUser,
  verifyPhoneOtpUser,
  USER_TYPES,
  LOGIN_STRINGS,
  MEMBERSHIP_BASE_PRICE,
  MEMBERSHIP_GST_AMOUNT,
  MEMBERSHIP_TOTAL_PRICE,
} from "./Auth.js";
import PaymentForm, { PAYMENT_DETAILS_DEFAULT, validatePaymentDetails } from "../../Common/PaymentForm/PaymentForm";
import GoogleLoginButton from "../../Common/GoogleLoginButton";
import { generateReceiptHTML } from "../Products/Esim/Edata.js";
import receiptLogo from "../../../assets/PNG/RecieptLogo.png";
import { COUNTRY_CODES } from "./ProfileLogic.js";
import UserService from "../../../services/userService";

// ── Feature Item ──────────────────────────────
function FeatureItem({ text }) {
  return (
    <div className="login-feature-item">
      <span className="login-feature-dot" />
      <span>{text}</span>
    </div>
  );
}

// ── Password Rules ─────────────────────────────
const PASSWORD_RULES = [
  { label: "8–16 characters",        test: (p) => p.length >= 8 && p.length <= 16 },
  { label: "Uppercase letter (A–Z)", test: (p) => /[A-Z]/.test(p) },
  { label: "Lowercase letter (a–z)", test: (p) => /[a-z]/.test(p) },
  { label: "Number (0–9)",           test: (p) => /[0-9]/.test(p) },
  { label: "Special character (! @ # $ % ^ & *)", test: (p) => /[!@#$%^&*]/.test(p) },
];

function validatePassword(pw) {
  for (const r of PASSWORD_RULES) {
    if (!r.test(pw)) return r.label;
  }
  return null;
}

function getStrengthLabel(met) {
  if (met <= 1) return "Very Weak";
  if (met === 2) return "Weak";
  if (met === 3) return "Medium";
  if (met === 4) return "Strong";
  return "Very Strong";
}

function PasswordRules({ password }) {
  const met = PASSWORD_RULES.filter((r) => r.test(password)).length;
  const pct = Math.round((met / PASSWORD_RULES.length) * 100);
  const barColor = pct < 60 ? "#eab308" : pct < 80 ? "#84cc16" : "#22c55e";
  const left = PASSWORD_RULES.slice(0, 3);
  const right = PASSWORD_RULES.slice(3);
  function renderRule(r, i) {
    const ok = r.test(password);
    return (
      <span key={i} className={`login-password-rule${ok ? " ok" : ""}`}>
        {ok ? "✓" : "✗"} {r.label}
      </span>
    );
  }
  return (
    <div className="login-password-rules">
      <div className="login-password-strength-bar">
        <div className="login-password-strength-fill" style={{ width: `${pct}%`, background: barColor }} />
      </div>
      <div className="login-password-rules-list">
        <div className="login-password-strength-heading" style={{ color: barColor }}>{getStrengthLabel(met)}</div>
        <div className="login-password-rules-columns">
          <div className="login-password-rules-col">
            {left.map((r, i) => renderRule(r, i))}
          </div>
          <div className="login-password-rules-col">
            {right.map((r, i) => renderRule(r, i + left.length))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Auth Modal ────────────────────────────────
function AuthModal({ type, role, onClose, onSuccess, skipOtp = false, setPaymentModal }) {
  const [mode, setMode]               = useState(type); // "login" | "signup"
  const [firstName, setFirstName]       = useState("");
  const [lastName, setLastName]         = useState("");
  const [companyName, setCompanyName] = useState("");
  const [country, setCountry]         = useState(LOGIN_STRINGS.DEFAULT_COUNTRY);
  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError]             = useState("");
  const [success, setSuccess]         = useState("");
  const [loading, setLoading]         = useState(false);

  const { finalizeAuth } = useAuth();
  const [authMethod, setAuthMethod] = useState("email");
  const [phoneCode, setPhoneCode] = useState(LOGIN_STRINGS.DEFAULT_PHONE_CODE);
  const [phone, setPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [timer, setTimer] = useState(120);
  const [timerActive, setTimerActive] = useState(false);
  const [googlePendingResult, setGooglePendingResult] = useState(null);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [pendingSignupEmail, setPendingSignupEmail] = useState("");
  const [verificationMethod, setVerificationMethod] = useState("choice");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(null); // "password" | "email" | "otp" | null
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [forgotMethod, setForgotMethod] = useState("email"); // "email" | "phone"
  const timerRef = useRef(null);
  const otpInputsRef = useRef([]);

  const roleLabel = USER_TYPES.find((u) => u.key === role)?.label || role;

  useEffect(() => {
    if (timerActive && timer > 0) {
      timerRef.current = setTimeout(() => setTimer((t) => t - 1), 1000);
    } else if (timerActive && timer === 0) {
      setTimerActive(false);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [timerActive, timer]);

  function formatTime(sec) {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  }

  function resetPhoneOtpState() {
    setOtpSent(false);
    setOtpValue("");
    setTimer(120);
    setTimerActive(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  }

  async function handleSendPhoneOtp(e) {
    e.preventDefault();
    if (!phone || phone.length < 4) { setError("Please enter a valid phone number."); return; }
    setError(""); setLoading(true);
    const result = await sendPhoneOtpUser({
      phone, phoneCode,
      purpose: mode,
      name: mode === "signup" ? `${firstName} ${lastName}`.trim() : undefined,
      role: mode === "signup" ? role : undefined,
      companyName: mode === "signup" && role === "agent" ? companyName : undefined,
      country: mode === "signup" && role === "agent" ? country : undefined,
    });
    setLoading(false);
    if (!result.success) { setError(result.error); return; }
    setOtpSent(true);
    setTimer(120);
    setTimerActive(true);
    setTimeout(() => otpInputsRef.current[0]?.focus(), 100);
  }

  async function handleVerifyPhoneOtp(e) {
    e.preventDefault();
    if (!otpValue || otpValue.length !== 6) { setError("Please enter the 6-digit OTP."); return; }
    setError(""); setLoading(true);
    const result = await verifyPhoneOtpUser({
      phone, phoneCode, otp: otpValue, purpose: mode,
      name: mode === "signup" ? `${firstName} ${lastName}`.trim() : undefined,
      role: mode === "signup" ? role : undefined,
      companyName: mode === "signup" && role === "agent" ? companyName : undefined,
      country: mode === "signup" && role === "agent" ? country : undefined,
    });
    setLoading(false);
    if (!result.success) { setError(result.error); return; }
    setTimerActive(false);
    finalizeAuth({ accessToken: result.accessToken, refreshToken: result.refreshToken, user: result.user });
    onSuccess(result.user);
  }

  function handleOtpDigitChange(index, value) {
    if (!/^\d*$/.test(value)) return;
    const newOtp = otpValue.split("");
    newOtp[index] = value.slice(-1);
    const joined = newOtp.join("").slice(0, 6);
    setOtpValue(joined);
    if (value && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  }

  function handleOtpKeyDown(index, e) {
    if (e.key === "Backspace" && !otpValue[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  }

  function handleOtpPaste(e) {
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) setOtpValue(pasted);
  }

  async function handleResendOtp(e) {
    e.preventDefault();
    setError("");
    setOtpValue("");
    const result = await sendPhoneOtpUser({
      phone, phoneCode,
      purpose: mode,
      name: mode === "signup" ? `${firstName} ${lastName}`.trim() : undefined,
      role: mode === "signup" ? role : undefined,
      companyName: mode === "signup" && role === "agent" ? companyName : undefined,
      country: mode === "signup" && role === "agent" ? country : undefined,
    });
    if (!result.success) { setError(result.error); return; }
    setTimer(120);
    setTimerActive(true);
  }

  function switchAuthMethod(method) {
    setAuthMethod(method);
    resetPhoneOtpState();
    setGooglePendingResult(null);
    setEmailOtpSent(false);
    setPendingSignupEmail("");
    setOtpValue("");
    setConfirmPassword("");
    setFirstName("");
    setLastName("");
    setSuccess("");
    setError("");
  }

  async function handleLogin(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    await new Promise((r) => setTimeout(r, 600));
    const result = await loginUser({ email, password });
    setLoading(false);
    if (!result.success) { setError(result.error); return; }
    finalizeAuth({ accessToken: result.accessToken, refreshToken: result.refreshToken, user: result.user });
    onSuccess(result.user);
  }

  async function handleSignup(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    if (password !== confirmPassword) {
      setLoading(false);
      return;
    }
    const pwErr = validatePassword(password);
    if (pwErr) {
      setLoading(false);
      setError(pwErr);
      return;
    }
    await new Promise((r) => setTimeout(r, 600));
    const result = await signupUser({ name: `${firstName} ${lastName}`.trim(), email, password, role, companyName: role === "agent" ? companyName : undefined, country: role === "agent" ? country : undefined });

    setLoading(false);
    if (!result.success) { setError(result.error); return; }

    if (skipOtp) {
      const loginResult = await loginUser({ email, password });
      if (!loginResult.success) { setError(loginResult.error); return; }
      finalizeAuth({ accessToken: loginResult.accessToken, refreshToken: loginResult.refreshToken, user: loginResult.user });
      setSuccess(LOGIN_STRINGS.SUCCESS_SIGNUP_MEMBERSHIP);
      setTimeout(() => { onSuccess(loginResult.user); }, 1500);
      return;
    }

    setPendingSignupEmail(email);
    setEmailOtpSent(true);
    setVerificationMethod("choice");
    setSuccess("Account created! An OTP was sent to your email. Choose how to complete verification.");
  }

  async function handleVerifyEmailOtp(e) {
    e.preventDefault();
    if (!otpValue || otpValue.length !== 6) { setError("Please enter the 6-digit OTP."); return; }
    setError(""); setLoading(true);
    const result = await verifyEmailOtpAndLogin(pendingSignupEmail, password, otpValue);
    setLoading(false);
    if (!result.success) { setError(result.error); return; }
    setEmailOtpSent(false);
    setPendingSignupEmail("");
    finalizeAuth({ accessToken: result.accessToken, refreshToken: result.refreshToken, user: result.user });
    onSuccess(result.user);
  }

  async function handleSignupPhoneSendOtp(e) {
    e?.preventDefault();
    if (!phone || phone.length < 4) { setError("Please enter a valid phone number."); return; }
    setError(""); setOtpValue(""); setOtpSent(true);
    const r = await sendPhoneOtpUser({ phone, phoneCode, purpose: "signup_verify" });
    if (!r.success) { setOtpSent(false); setError(r.error); return; }
    setSuccess("OTP sent. Please check your phone.");
    setTimer(120);
    setTimerActive(true);
    setTimeout(() => otpInputsRef.current[0]?.focus(), 200);
  }

  async function handleSignupPhoneOtpVerify(e) {
    e?.preventDefault();
    if (!otpValue || otpValue.length !== 6) { setError("Please enter the 6-digit OTP."); return; }
    setError(""); setLoading(true);
    const result = await verifyPhoneForSignup({ phone, phoneCode, otp: otpValue, email: pendingSignupEmail, password });
    setLoading(false);
    if (!result.success) { setError(result.error); return; }
    setOtpSent(false);
    setTimerActive(false);
    finalizeAuth({ accessToken: result.accessToken, refreshToken: result.refreshToken, user: result.user });
    onSuccess(result.user);
  }

  const handleGoogleCredential = useCallback(async (idToken) => {
    setError(""); setLoading(true);
    const result = await googleSignIn(idToken, role, companyName || undefined, country || undefined);
    setLoading(false);
    if (!result.success) { setError(result.error); return; }
    const authResult = await completeGoogleSignup(result.rawResult);
    finalizeAuth(authResult);
    onSuccess(authResult.user);
    UserService.fixAuthProvider().catch(() => {});
  }, [role, companyName, country, onSuccess]);

  const handleGoogleError = useCallback((msg) => setError(msg), []);

  async function handleGooglePhoneVerify(e) {
    e.preventDefault();
    if (!phone || phone.length < 4) { setError("Please enter a valid phone number."); return; }
    if (!otpValue || otpValue.length !== 6) { setError("Please enter the 6-digit OTP."); return; }
    setError(""); setLoading(true);
    const otpResult = await verifyPhoneOtpUser({
      phone, phoneCode, otp: otpValue, purpose: "verify_phone",
    });
    setLoading(false);
    if (!otpResult.success) { setError(otpResult.error); return; }
    try {
      await UserService.updateProfile({ phone, phoneCode });
    } catch { /* non-critical */ }
    const raw = googlePendingResult;
    setGooglePendingResult(null);
    const authResult = completeGoogleSignup(raw);
    finalizeAuth(authResult);
    onSuccess(authResult.user);
  }

  function resetForgotState() {
    setForgotStep(null);
    setForgotEmail("");
    setForgotNewPassword("");
    setForgotConfirmPassword("");
    setForgotMethod("email");
    setOtpValue("");
    setOtpSent(false);
  }

  async function handleForgotStart() {
    setError(""); setSuccess("");
    setForgotStep("password");
  }

  async function handleForgotSubmitPassword(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    if (forgotNewPassword !== forgotConfirmPassword) {
      setLoading(false); setError("Passwords do not match."); return;
    }
    const pwErr = validatePassword(forgotNewPassword);
    if (pwErr) { setLoading(false); setError(pwErr); return; }
    setLoading(false);
    setForgotStep("choice");
  }

  async function handleForgotSendOtp(e, method) {
    if (e?.preventDefault) e.preventDefault();
    if (method === "email") {
      if (!forgotEmail) { setError("Please enter your email."); return; }
      setError(""); setLoading(true);
      const r = await forgotPasswordOtp(forgotEmail);
      setLoading(false);
      if (!r.success) { setError(r.error); return; }
      setSuccess("OTP sent to your email. Please check your inbox.");
      setForgotStep("otp");
    } else {
      if (!phone || phone.length < 4) { setError("Please enter a valid phone number."); return; }
      setError(""); setOtpSent(true);
      const r = await sendPhoneOtpUser({ phone, phoneCode, purpose: "reset" });
      if (!r.success) { setOtpSent(false); setError(r.error); return; }
      setSuccess("OTP sent to your phone.");
      setForgotStep("otp");
      setTimer(120); setTimerActive(true);
      setTimeout(() => otpInputsRef.current[0]?.focus(), 200);
    }
  }

  async function handleForgotVerifyOtp() {
    const otp = otpValue;
    if (!otp || otp.length !== 6) { setError("Please enter the 6-digit OTP."); return; }
    setError(""); setLoading(true);
    let r;
    if (forgotMethod === "email") {
      r = await resetPassword(forgotEmail, otp, forgotNewPassword);
    } else {
      r = await resetPasswordByPhone(phone, phoneCode, otp, forgotNewPassword);
    }
    setLoading(false);
    if (!r.success) { setError(r.error); return; }
    setSuccess("Password reset successful! You can now sign in.");
    resetForgotState();
  }

  return (
    <div
      className="login-modal-overlay"
      onClick={(e) => !skipOtp && e.target === e.currentTarget && onClose()}
    >
      <div className="login-modal-box" data-aos="zoom-in">
        <div className="login-modal-top">
          <h3>{forgotStep ? "Reset Password" : mode === "login" ? `Sign in with ${authMethod === "email" ? "Email" : "Phone"}` : `Create Account with ${authMethod === "email" ? "Email" : "Phone"}`}</h3>
          {!skipOtp && (
            <button className="login-modal-close" onClick={onClose}>
              &#215;
            </button>
          )}
        </div>

        <div className="login-role-badge">{roleLabel}{LOGIN_STRINGS.ROLE_BADGE_ACCOUNT}</div>

        {error   && <div className="login-form-error">{error}</div>}
        {success && <div className="login-form-success">{success}</div>}

        {/* ── Auth Method Tabs ── */}
        {!googlePendingResult && !emailOtpSent && !otpSent && !forgotStep && (
          <div className="login-auth-tabs">
            <button
              className={`login-auth-tab${authMethod === "email" ? " active" : ""}`}
              onClick={() => switchAuthMethod("email")}
            >
              Email
            </button>
            <button
              className={`login-auth-tab${authMethod === "phone" ? " active" : ""}`}
              onClick={() => switchAuthMethod("phone")}
            >
              Phone
            </button>
          </div>
        )}

        <div style={{ display: forgotStep ? "none" : "" }}>
        {/* ── GOOGLE SIGNUP PHONE OTP ── */}
        {googlePendingResult && (
          <form onSubmit={handleGooglePhoneVerify}>
            <div className="login-form-group">
              <label>Phone Number</label>
              <div className="login-phone-input-row">
                <select
                  className="login-phone-code-select"
                  value={phoneCode}
                  onChange={(e) => setPhoneCode(e.target.value)}
                >
                  {COUNTRY_CODES.map((cc) => (
                    <option key={cc.code} value={cc.code}>{cc.code}</option>
                  ))}
                </select>
                <input
                  type="tel"
                  placeholder="Enter your phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                  disabled={otpSent}
                  required
                  className="login-phone-input"
                />
              </div>
            </div>

            {!otpSent ? (
              <button className="login-btn login-btn-primary" type="button" disabled={loading} onClick={async () => {
                if (!phone || phone.length < 4) { setError("Please enter a valid phone number."); return; }
                setError(""); setLoading(true);
                const r = await sendPhoneOtpUser({ phone, phoneCode, purpose: "verify_phone" });
                setLoading(false);
                if (!r.success) { setError(r.error); return; }
                setOtpSent(true); setTimer(120); setTimerActive(true);
                setTimeout(() => otpInputsRef.current[0]?.focus(), 100);
              }}>
                {loading ? <span className="login-spinner" /> : "Send OTP"}
              </button>
            ) : (
              <>
                <div className="login-otp-section">
                  <p className="login-otp-hint">Enter the 6-digit OTP sent to {phoneCode} {phone}</p>
                  <div className="login-otp-inputs" onPaste={(e) => {
                    e.preventDefault();
                    const p = (e.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "").slice(0, 6);
                    if (p) setOtpValue(p);
                  }}>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <input
                        key={i}
                        ref={(el) => (otpInputsRef.current[i] = el)}
                        className="login-otp-input"
                        type="text" inputMode="numeric" maxLength={1}
                        value={otpValue[i] || ""}
                        onChange={(e) => {
                          if (!/^\d*$/.test(e.target.value)) return;
                          const newOtp = otpValue.split("");
                          newOtp[i] = e.target.value.slice(-1);
                          setOtpValue(newOtp.join("").slice(0, 6));
                          if (e.target.value && i < 5) otpInputsRef.current[i + 1]?.focus();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace" && !otpValue[i] && i > 0) otpInputsRef.current[i - 1]?.focus();
                        }}
                        autoFocus={i === 0}
                      />
                    ))}
                  </div>
                  <div className="login-otp-timer">
                    {timerActive ? (
                      <span className="login-otp-timer-text">Resend in {formatTime(timer)}</span>
                    ) : (
                      <button className="login-otp-resend-btn" type="button" onClick={async () => {
                        setError("");
                        setOtpValue("");
                        const r = await sendPhoneOtpUser({ phone, phoneCode, purpose: "verify_phone" });
                        if (!r.success) { setError(r.error); return; }
                        setTimer(120); setTimerActive(true);
                      }}>Resend</button>
                    )}
                  </div>
                </div>
                <button className="login-btn login-btn-primary" type="submit" disabled={loading}>
                  {loading ? <span className="login-spinner" /> : "Complete Registration"}
                </button>
              </>
            )}
          </form>
        )}

        {/* ── EMAIL AUTH (Google button always mounted to avoid re-initialization) ── */}
        {!googlePendingResult && authMethod === "email" && !(mode === "signup" && emailOtpSent) && (
          <>
            <div className="login-google-btn-wrapper" style={{ position: 'relative' }}>
              <div style={{ opacity: loading ? 0.3 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
                <GoogleLoginButton onSuccess={handleGoogleCredential} onError={handleGoogleError} label={mode === "signup" ? "signup" : "login"} />
              </div>
              {loading && <span className="login-spinner" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />}
            </div>
            <div className="login-divider">{LOGIN_STRINGS.OR_DIVIDER}</div>

            {mode === "login" && (
              <>
                <form onSubmit={handleLogin}>
                  <div className="login-form-group">
                    <label>{LOGIN_STRINGS.LOGIN_EMAIL_LABEL}</label>
                    <input
                      type="email"
                      placeholder={LOGIN_STRINGS.LOGIN_EMAIL_PLACEHOLDER}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="login-form-group">
                    <label>{LOGIN_STRINGS.LOGIN_PASSWORD_LABEL}</label>
                    <div className="login-password-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder={LOGIN_STRINGS.LOGIN_PASSWORD_PLACEHOLDER}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <button type="button" className="login-password-toggle" onClick={() => setShowPassword((p) => !p)} tabIndex={-1}>
                        {showPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        )}
                      </button>
                    </div>
                    <button type="button" className="login-forgot-link" onClick={handleForgotStart}>Forgot Password?</button>
                  </div>
                  <button className="login-btn login-btn-primary" type="submit" disabled={loading}>
                    {loading ? <span className="login-spinner" /> : LOGIN_STRINGS.LOGIN_SUBMIT}
                  </button>
                </form>
                {role === "member" ? (
                  <div className="login-toggle-auth">
                    {LOGIN_STRINGS.TOGGLE_NO_ACCOUNT}
                    <button onClick={() => { setPaymentModal({ role }); onClose(); }}>{LOGIN_STRINGS.TOGGLE_BUY_NOW}</button>
                  </div>
                ) : (
                  <div className="login-toggle-auth">
                    {LOGIN_STRINGS.TOGGLE_NO_ACCOUNT}
                    <button onClick={() => { setMode("signup"); setError(""); setSuccess(""); setGooglePendingResult(null); setEmailOtpSent(false); setPendingSignupEmail(""); setOtpValue(""); setConfirmPassword(""); setFirstName(""); setLastName(""); resetPhoneOtpState(); }}>{LOGIN_STRINGS.TOGGLE_SIGN_UP}</button>
                  </div>
                )}
              </>
            )}

            {mode === "signup" && (
              <>
                <form onSubmit={handleSignup}>
                <div className="login-form-group">
                  <label>{LOGIN_STRINGS.SIGNUP_NAME_LABEL}</label>
                  <div className="login-name-row">
                    <input
                      type="text"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                    <input
                      type="text"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                {role === "agent" && (
                  <>
                    <div className="login-form-group">
                      <label>{LOGIN_STRINGS.SIGNUP_COMPANY_LABEL}</label>
                      <input
                        type="text"
                        placeholder={LOGIN_STRINGS.SIGNUP_COMPANY_PLACEHOLDER}
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="login-form-group">
                      <label>{LOGIN_STRINGS.SIGNUP_COUNTRY_LABEL}</label>
                      <input
                        type="text"
                        placeholder={LOGIN_STRINGS.SIGNUP_COUNTRY_PLACEHOLDER}
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        required
                      />
                    </div>
                  </>
                )}
                
                <div className="login-form-group">
                  <label>{LOGIN_STRINGS.SIGNUP_EMAIL_LABEL}</label>
                  <input
                    type="email"
                    placeholder={LOGIN_STRINGS.SIGNUP_EMAIL_PLACEHOLDER}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="login-form-group">
                  <label>{LOGIN_STRINGS.SIGNUP_PASSWORD_LABEL}</label>
                  <div className="login-password-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder={LOGIN_STRINGS.SIGNUP_PASSWORD_PLACEHOLDER}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button type="button" className="login-password-toggle" onClick={() => setShowPassword((p) => !p)} tabIndex={-1}>
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                  <PasswordRules password={password} />
                </div>
                <div className="login-form-group">
                  <label>Confirm Password</label>
                  <div className="login-password-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button type="button" className="login-password-toggle" onClick={() => setShowConfirmPassword((p) => !p)} tabIndex={-1}>
                      {showConfirmPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <span className="login-password-mismatch">Passwords do not match</span>
                  )}
                </div>
                <button className="login-btn login-btn-primary" type="submit" disabled={loading || PASSWORD_RULES.some((r) => !r.test(password))}>
                  {loading ? <span className="login-spinner" /> : LOGIN_STRINGS.SIGNUP_SUBMIT}
                </button>
              </form>
              {!skipOtp && (
                <div className="login-toggle-auth">
                  {LOGIN_STRINGS.TOGGLE_HAVE_ACCOUNT}
                  <button onClick={() => { setMode("login"); setError(""); setSuccess(""); setGooglePendingResult(null); setEmailOtpSent(false); setPendingSignupEmail(""); setOtpValue(""); setConfirmPassword(""); setFirstName(""); setLastName(""); resetPhoneOtpState(); }}>{LOGIN_STRINGS.TOGGLE_SIGN_IN}</button>
                </div>
              )}
              </>
            )}
          </>
        )}

        {/* ── EMAIL SIGNUP CHOICE + OTP ── */}
        {authMethod === "email" && mode === "signup" && emailOtpSent && verificationMethod === "choice" && (
          <div className="login-verification-choice">
            <p className="login-otp-hint">Account created! Choose how to verify your email <strong>{pendingSignupEmail}</strong>:</p>
            <div className="login-choice-buttons">
              <button type="button" className="login-btn login-btn-choice" onClick={() => { setVerificationMethod("email"); }}>
                <span className="login-choice-text">Get OTP via Email</span>
              </button>
              <button type="button" className="login-btn login-btn-choice" onClick={() => { setVerificationMethod("phone"); }}>
                <span className="login-choice-text">Get OTP via Phone</span>
              </button>
            </div>
          </div>
        )}
        {authMethod === "email" && mode === "signup" && emailOtpSent && verificationMethod === "email" && (
          <form onSubmit={handleVerifyEmailOtp}>
            <p className="login-otp-hint">A 6-digit OTP has been sent to {pendingSignupEmail}. Please check your inbox and enter it below.</p>
            <div className="login-otp-inputs" onPaste={(e) => {
              e.preventDefault();
              const p = (e.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "").slice(0, 6);
              if (p) setOtpValue(p);
            }}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <input
                  key={i}
                  ref={(el) => (otpInputsRef.current[i] = el)}
                  className="login-otp-input"
                  type="text" inputMode="numeric" maxLength={1}
                  value={otpValue[i] || ""}
                  onChange={(e) => {
                    if (!/^\d*$/.test(e.target.value)) return;
                    const newOtp = otpValue.split("");
                    newOtp[i] = e.target.value.slice(-1);
                    setOtpValue(newOtp.join("").slice(0, 6));
                    if (e.target.value && i < 5) otpInputsRef.current[i + 1]?.focus();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !otpValue[i] && i > 0) otpInputsRef.current[i - 1]?.focus();
                  }}
                  autoFocus={i === 0}
                />
              ))}
            </div>
            <button className="login-btn login-btn-primary" type="submit" disabled={loading} style={{ marginTop: "16px" }}>
              {loading ? <span className="login-spinner" /> : "Verify OTP & Complete Signup"}
            </button>
            <div className="login-otp-timer" style={{ marginTop: "12px" }}>
              <button className="login-otp-resend-btn" type="button" onClick={async () => {
                setError(""); setOtpValue("");
                const r = await signupUser({ name: `${firstName} ${lastName}`.trim(), email: pendingSignupEmail, password, role, companyName: role === "agent" ? companyName : undefined, country: role === "agent" ? country : undefined });
                if (!r.success) { setError(r.error); return; }
                setSuccess("OTP resent. Please check your inbox.");
              }}>Resend</button>
            </div>
            <button type="button" className="login-otp-resend-btn" style={{ marginTop: "8px" }} onClick={() => { setVerificationMethod("choice"); setOtpValue(""); }}>Change verification method</button>
          </form>
        )}
        {authMethod === "email" && mode === "signup" && emailOtpSent && verificationMethod === "phone" && (
          <form onSubmit={otpSent ? handleSignupPhoneOtpVerify : handleSignupPhoneSendOtp}>
            <div className="login-form-group">
              <label>Phone Number</label>
              <div className="login-phone-input-row">
                <select
                  className="login-phone-code-select"
                  value={phoneCode}
                  onChange={(e) => setPhoneCode(e.target.value)}
                >
                  {COUNTRY_CODES.map((cc) => (
                    <option key={cc.code} value={cc.code}>{cc.code}</option>
                  ))}
                </select>
                <input
                  type="tel"
                  placeholder="Enter your phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                  disabled={otpSent}
                  required
                  className="login-phone-input"
                />
              </div>
            </div>

            {otpSent && (
              <>
                <div className="login-otp-inputs" onPaste={(e) => {
                  e.preventDefault();
                  const p = (e.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "").slice(0, 6);
                  if (p) setOtpValue(p);
                }}>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <input
                      key={i}
                      ref={(el) => (otpInputsRef.current[i] = el)}
                      className="login-otp-input"
                      type="text" inputMode="numeric" maxLength={1}
                      value={otpValue[i] || ""}
                      onChange={(e) => {
                        if (!/^\d*$/.test(e.target.value)) return;
                        const newOtp = otpValue.split("");
                        newOtp[i] = e.target.value.slice(-1);
                        setOtpValue(newOtp.join("").slice(0, 6));
                        if (e.target.value && i < 5) otpInputsRef.current[i + 1]?.focus();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" && !otpValue[i] && i > 0) otpInputsRef.current[i - 1]?.focus();
                      }}
                      autoFocus={i === 0}
                    />
                  ))}
                </div>
                <div className="login-otp-timer">
                  {timer > 0 ? (
                    <span className="login-otp-timer-text">Resend in {formatTime(timer)}</span>
                  ) : (
                    <button className="login-otp-resend-btn" type="button" onClick={handleSignupPhoneSendOtp}>Resend</button>
                  )}
                </div>
              </>
            )}

            <button className="login-btn login-btn-primary" type="submit" disabled={loading} style={{ marginTop: "16px" }}>
              {loading ? <span className="login-spinner" /> : otpSent ? "Verify Phone & Complete Signup" : "Send OTP"}
            </button>
            <button type="button" className="login-otp-resend-btn" style={{ marginTop: "8px" }} onClick={() => { setVerificationMethod("choice"); setOtpSent(false); setOtpValue(""); }}>Change verification method</button>
          </form>
        )}

        {/* ── PHONE LOGIN ── */}
        {!googlePendingResult && authMethod === "phone" && mode === "login" && (
          <form onSubmit={otpSent ? handleVerifyPhoneOtp : handleSendPhoneOtp}>
            <div className="login-form-group">
              <label>Phone Number</label>
              <div className="login-phone-input-row">
                <select
                  className="login-phone-code-select"
                  value={phoneCode}
                  onChange={(e) => setPhoneCode(e.target.value)}
                >
                  {COUNTRY_CODES.map((cc) => (
                    <option key={cc.code} value={cc.code}>{cc.code}</option>
                  ))}
                </select>
                <input
                  type="tel"
                  placeholder="Enter your phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                  disabled={otpSent}
                  required
                  className="login-phone-input"
                />
              </div>
            </div>

            {otpSent && (
              <>
                <div className="login-otp-section">
                  <p className="login-otp-hint">Enter the 6-digit OTP sent to {phoneCode} {phone}</p>
                  <div className="login-otp-inputs" onPaste={handleOtpPaste}>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <input
                        key={i}
                        ref={(el) => (otpInputsRef.current[i] = el)}
                        className="login-otp-input"
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={otpValue[i] || ""}
                        onChange={(e) => handleOtpDigitChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        autoFocus={i === 0}
                      />
                    ))}
                  </div>
                  <div className="login-otp-timer">
                    {timerActive ? (
                      <span className="login-otp-timer-text">Resend in {formatTime(timer)}</span>
                    ) : (
                      <button className="login-otp-resend-btn" onClick={handleResendOtp} type="button">
                        Resend
                      </button>
                    )}
                  </div>
                </div>
                <button className="login-btn login-btn-primary" type="submit" disabled={loading}>
                  {loading ? <span className="login-spinner" /> : "Verify OTP"}
                </button>
              </>
            )}

            {!otpSent && (
              <button className="login-btn login-btn-primary" type="submit" disabled={loading}>
                {loading ? <span className="login-spinner" /> : "Send OTP"}
              </button>
            )}
          </form>
        )}

        {/* ── PHONE SIGNUP ── */}
        {!googlePendingResult && authMethod === "phone" && mode === "signup" && (
          <form onSubmit={otpSent ? handleVerifyPhoneOtp : handleSendPhoneOtp}>
            {!otpSent && (
              <>
                <div className="login-name-row">
                  <input
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
                <button className="login-btn login-btn-primary" type="submit" disabled={loading}>
                  {loading ? <span className="login-spinner" /> : "Send OTP"}
                </button>
              </>
            )}
          </form>
        )}
        </div>

        {/* ── FORGOT PASSWORD ── */}
        {forgotStep && (
          <div className="login-forgot-flow">
            {forgotStep === "password" && (
              <form onSubmit={handleForgotSubmitPassword}>
                <div className="login-form-group">
                  <label>New Password</label>
                  <div className="login-password-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="New password"
                      value={forgotNewPassword}
                      onChange={(e) => setForgotNewPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button type="button" className="login-password-toggle" onClick={() => setShowPassword((p) => !p)} tabIndex={-1}>
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                </div>
                <PasswordRules password={forgotNewPassword} />
                <div className="login-form-group">
                  <label>Confirm New Password</label>
                  <div className="login-password-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter new password"
                      value={forgotConfirmPassword}
                      onChange={(e) => setForgotConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button type="button" className="login-password-toggle" onClick={() => setShowConfirmPassword((p) => !p)} tabIndex={-1}>
                      {showConfirmPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                  {forgotConfirmPassword && forgotNewPassword !== forgotConfirmPassword && (
                    <span className="login-password-mismatch">Passwords do not match</span>
                  )}
                </div>
                <button className="login-btn login-btn-primary" type="submit" disabled={loading || PASSWORD_RULES.some((r) => !r.test(forgotNewPassword))} style={{ marginTop: "12px" }}>
                  {loading ? <span className="login-spinner" /> : "Next"}
                </button>
              </form>
            )}
            {forgotStep === "choice" && (
              <div className="login-verification-choice">
                <p className="login-otp-hint">How would you like to receive the reset OTP?</p>
                <div className="login-choice-buttons">
                  <button type="button" className="login-btn login-btn-choice" onClick={() => { setForgotMethod("email"); setForgotStep("email"); }}>
                    <span className="login-choice-text">Get OTP via Email</span>
                  </button>
                  <button type="button" className="login-btn login-btn-choice" onClick={() => { setForgotMethod("phone"); setForgotStep("phone"); }}>
                    <span className="login-choice-text">Get OTP via Phone</span>
                  </button>
                </div>
              </div>
            )}
            {forgotStep === "email" && (
              <form onSubmit={(e) => { e.preventDefault(); handleForgotSendOtp(e, "email"); }}>
                <p className="login-otp-hint">Enter your email to receive a reset OTP.</p>
                <div className="login-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </div>
                <button className="login-btn login-btn-primary" type="submit" disabled={loading} style={{ marginTop: "12px" }}>
                  {loading ? <span className="login-spinner" /> : "Send OTP"}
                </button>
                <button type="button" className="login-otp-resend-btn" style={{ marginTop: "8px" }} onClick={() => setForgotStep("choice")}>Change verification method</button>
              </form>
            )}
            {forgotStep === "phone" && (
              <form onSubmit={(e) => { e.preventDefault(); handleForgotSendOtp(e, "phone"); }}>
                <p className="login-otp-hint">Enter your phone number to receive a reset OTP.</p>
                <div className="login-form-group">
                  <label>Phone Number</label>
                  <div className="login-phone-input-row">
                    <select
                      className="login-phone-code-select"
                      value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value)}
                    >
                      {COUNTRY_CODES.map((cc) => (
                        <option key={cc.code} value={cc.code}>{cc.code}</option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      placeholder="Enter your phone number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                      required
                      className="login-phone-input"
                    />
                  </div>
                </div>
                <button className="login-btn login-btn-primary" type="submit" disabled={loading} style={{ marginTop: "12px" }}>
                  {loading ? <span className="login-spinner" /> : "Send OTP"}
                </button>
                <button type="button" className="login-otp-resend-btn" style={{ marginTop: "8px" }} onClick={() => setForgotStep("choice")}>Change verification method</button>
              </form>
            )}
            {forgotStep === "otp" && (
              <form onSubmit={(e) => { e.preventDefault(); handleForgotVerifyOtp(); }}>
                <p className="login-otp-hint">Enter the 6-digit OTP sent to your {forgotMethod === "email" ? "email" : "phone"}.</p>
                <div className="login-otp-inputs" onPaste={(e) => {
                  e.preventDefault();
                  const p = (e.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "").slice(0, 6);
                  if (p) setOtpValue(p);
                }}>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <input
                      key={i}
                      ref={(el) => (otpInputsRef.current[i] = el)}
                      className="login-otp-input"
                      type="text" inputMode="numeric" maxLength={1}
                      value={otpValue[i] || ""}
                      onChange={(e) => {
                        if (!/^\d*$/.test(e.target.value)) return;
                        const newOtp = otpValue.split("");
                        newOtp[i] = e.target.value.slice(-1);
                        setOtpValue(newOtp.join("").slice(0, 6));
                        if (e.target.value && i < 5) otpInputsRef.current[i + 1]?.focus();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" && !otpValue[i] && i > 0) otpInputsRef.current[i - 1]?.focus();
                      }}
                      autoFocus={i === 0}
                    />
                  ))}
                </div>
                <div className="login-otp-timer" style={{ marginTop: "12px" }}>
                  {timer > 0 ? (
                    <span className="login-otp-timer-text">Resend in {formatTime(timer)}</span>
                  ) : (
                    <button className="login-otp-resend-btn" type="button" onClick={() => handleForgotSendOtp(null, forgotMethod)}>Resend</button>
                  )}
                </div>
                <button className="login-btn login-btn-primary" type="submit" disabled={loading} style={{ marginTop: "8px" }}>
                  {loading ? <span className="login-spinner" /> : "Verify OTP & Reset Password"}
                </button>
              </form>
            )}
            <button type="button" className="login-forgot-back" onClick={() => { resetForgotState(); setError(""); setOtpValue(""); }}>Back to Sign In</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Membership Payment Modal ─────────────────
function MembershipPaymentModal({ role, onClose, onSuccess }) {
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentDetails, setPaymentDetails] = useState({ ...PAYMENT_DETAILS_DEFAULT });
  const [consentChecked, setConsentChecked] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneCode, setPhoneCode] = useState(LOGIN_STRINGS.DEFAULT_PHONE_CODE);
  const [phone, setPhone] = useState("");

  const roleLabel = USER_TYPES.find(u => u.key === role)?.label || role;
  const basePrice = MEMBERSHIP_BASE_PRICE;
  const gstAmount = MEMBERSHIP_GST_AMOUNT;
  const totalAmount = MEMBERSHIP_TOTAL_PRICE;

  React.useEffect(() => {
    setConsentChecked(false);
  }, [paymentMethod]);

  function handleDetailChange(e) {
    const { name, value } = e.target;
    setPaymentDetails(prev => ({ ...prev, [name]: value }));
  }

  async function handlePayment(e) {
    e.preventDefault();
    setError("");

    const validationError = validatePaymentDetails(paymentMethod, paymentDetails);
    if (validationError) { setError(validationError); return; }

    setLoading(true);
    await new Promise(r => setTimeout(r, 1500));
    setLoading(false);

    // Build payment method display with masked details
    let methodDisplay = paymentMethod;
    if (paymentMethod === 'debit' || paymentMethod === 'credit') {
      const last4 = paymentDetails.cardNum ? paymentDetails.cardNum.replace(/\s/g, '').slice(-4) : '****';
      methodDisplay = `${paymentMethod}|${LOGIN_STRINGS.CARD_ENDING_IN}${last4}`;
    } else if (paymentMethod === 'upi') {
      methodDisplay = `UPI|${paymentDetails.upiId || LOGIN_STRINGS.NA_FALLBACK}`;
    } else if (paymentMethod === 'netbanking') {
      methodDisplay = `Net Banking|${paymentDetails.bankName || LOGIN_STRINGS.NA_FALLBACK}`;
    }

    onSuccess({
      methodDisplay,
      firstName,
      lastName,
      phoneCode,
      phone,
    });
  }

  return (
    <div className="login-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="login-modal-box login-payment-modal" data-aos="zoom-in">
        <div className="login-modal-top">
          <h3>{LOGIN_STRINGS.PAYMENT_TITLE}</h3>
          <button className="login-modal-close" onClick={onClose}>&#215;</button>
        </div>

        <div className="login-role-badge">{roleLabel}{LOGIN_STRINGS.ROLE_BADGE_MEMBERSHIP}</div>

          <div className="login-payment-breakdown">
            <div className="login-payment-row">
              <span>{LOGIN_STRINGS.PAYMENT_BASE_PRICE}</span>
              <span>{LOGIN_STRINGS.PAYMENT_CURRENCY}{basePrice}</span>
            </div>
            <div className="login-payment-row">
              <span>{LOGIN_STRINGS.PAYMENT_GST}</span>
              <span>{LOGIN_STRINGS.PAYMENT_CURRENCY}{gstAmount}</span>
            </div>
            <div className="login-payment-divider"></div>
            <div className="login-payment-row login-payment-total">
              <span>{LOGIN_STRINGS.PAYMENT_TOTAL}</span>
              <span>{LOGIN_STRINGS.PAYMENT_CURRENCY}{totalAmount}</span>
            </div>
          </div>

        <div className="login-billing-section">
          <h4 className="login-billing-title">{LOGIN_STRINGS.PAYMENT_BILLING_TITLE}</h4>
          <div className="login-billing-row">
            <div className="login-form-group login-billing-half">
              <label>{LOGIN_STRINGS.PAYMENT_FIRST_NAME_LABEL}</label>
              <input
                type="text"
                placeholder={LOGIN_STRINGS.PAYMENT_FIRST_NAME_PLACEHOLDER}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="login-form-group login-billing-half">
              <label>{LOGIN_STRINGS.PAYMENT_LAST_NAME_LABEL}</label>
              <input
                type="text"
                placeholder={LOGIN_STRINGS.PAYMENT_LAST_NAME_PLACEHOLDER}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="login-form-group">
            <label>{LOGIN_STRINGS.PAYMENT_PHONE_LABEL}</label>
            <div className="login-phone-row">
              <select className="login-phone-code" value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)}>
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                ))}
              </select>
              <input
                type="tel"
                placeholder={LOGIN_STRINGS.PAYMENT_PHONE_PLACEHOLDER}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className="login-phone-input"
              />
            </div>
          </div>
        </div>

        {error && <div className="login-form-error">{error}</div>}

        <form onSubmit={handlePayment}>
          <PaymentForm
            selectedMethod={paymentMethod}
            onMethodSelect={setPaymentMethod}
            details={paymentDetails}
            onDetailChange={handleDetailChange}
            hideMethods={["reward"]}
          />

          {/* Consent Form - Only show after payment method is selected */}
          {paymentMethod && (
            <div className="login-consent-form">
              <h4 className="login-consent-title">{LOGIN_STRINGS.CONSENT_TITLE}</h4>
              <p className="login-consent-intro">
                {LOGIN_STRINGS.CONSENT_INTRO}
              </p>
              <div className="login-consent-list">
                <p>{LOGIN_STRINGS.CONSENT_LIST_INTRO}</p>
                <ul>
                  <li>{LOGIN_STRINGS.CONSENT_BULLET_1}</li>
                  <li>{LOGIN_STRINGS.CONSENT_BULLET_2}</li>
                  <li>{LOGIN_STRINGS.CONSENT_BULLET_3}</li>
                  <li>{LOGIN_STRINGS.CONSENT_BULLET_4_PREFIX}<a href={LOGIN_STRINGS.CONSENT_TERMS_URL} target="_blank" rel="noopener noreferrer">{LOGIN_STRINGS.CONSENT_BULLET_4_TERMS}</a>{LOGIN_STRINGS.CONSENT_BULLET_4_AND}<a href={LOGIN_STRINGS.CONSENT_PRIVACY_URL} target="_blank" rel="noopener noreferrer">{LOGIN_STRINGS.CONSENT_BULLET_4_PRIVACY}</a>{LOGIN_STRINGS.CONSENT_BULLET_4_SUFFIX}</li>
                  <li>{LOGIN_STRINGS.CONSENT_BULLET_5}</li>
                  <li>{LOGIN_STRINGS.CONSENT_BULLET_6}</li>
                </ul>
              </div>
              <div className="login-consent-checkbox" onClick={() => setConsentChecked(!consentChecked)}>
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  required
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: 'none' }}
                />
                <span className="login-consent-custom-box">{consentChecked ? '☑' : '☐'}</span>
                <span className="login-consent-text">{LOGIN_STRINGS.CONSENT_CHECKBOX_LABEL}</span>
              </div>
            </div>
          )}

          <button className="login-btn login-btn-primary login-payment-btn" type="submit" disabled={loading || !paymentMethod || !consentChecked}>
            {loading ? <span className="login-spinner" /> : LOGIN_STRINGS.PAYMENT_SUBMIT(totalAmount)}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Receipt Modal ──────────────────────────
function ReceiptModal({ data, onCreateAccount, onClose }) {
  const roleLabel = USER_TYPES.find(u => u.key === data.role)?.label || data.role;

  function getPaymentMethodLabel(method) {
    return LOGIN_STRINGS.PAYMENT_METHOD_LABELS[method] || method;
  }

  async function handleDownloadPDF() {
    try {
      const { jsPDF } = await import("jspdf");
      const html2canvas = (await import("html2canvas")).default;
      const toBase64 = async (url) => {
        const resp = await fetch(url);
        const blob = await resp.blob();
        return new Promise((r) => {
          const reader = new FileReader();
          reader.onloadend = () => r(reader.result);
          reader.readAsDataURL(blob);
        });
      };
      let logoSrc = null;
      try { logoSrc = await toBase64(receiptLogo); } catch { logoSrc = null; }

      const basePrice = data.basePrice || MEMBERSHIP_BASE_PRICE;
      const gstAmount = data.gstAmount || MEMBERSHIP_GST_AMOUNT;
      const totalAmount = data.totalAmount || MEMBERSHIP_TOTAL_PRICE;

      const item = {
        label: `${roleLabel}${LOGIN_STRINGS.ROLE_BADGE_MEMBERSHIP}`,
        countryName: "",
        price: basePrice,
        data: LOGIN_STRINGS.RECEIPT_ITEM_DATA,
        validity: 1,
        type: LOGIN_STRINGS.RECEIPT_ITEM_TYPE,
      };

      const html = generateReceiptHTML({
        item, quantity: 1,
        payMethod: data.paymentMethodRaw || "upi",
        billingDetails: {
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          phoneCode: data.phoneCode || "+91",
          phone: data.phone || "",
          email: "",
          country: "",
          address: "",
          city: "",
          pincode: "",
          accountType: `${roleLabel}${LOGIN_STRINGS.ROLE_BADGE_MEMBERSHIP}`,
          companyName: "",
        },
        orderId: data.transactionId,
        currency: LOGIN_STRINGS.CURRENCY_CODE,
        appliedDiscount: 0,
        logoSrc, pdf: true, inr: true,
        upiId: data.paymentMethodRaw === "upi" ? data.paymentDetails : "",
        bank: data.paymentMethodRaw === "netbanking" ? data.paymentDetails : "",
        card: data.paymentMethodRaw === "debit" || data.paymentMethodRaw === "credit" ? { number: data.paymentDetails || "" } : { number: "" },
        simType: "",
        applicants: [],
        overrides: {
          subtotal: basePrice,
          tax: gstAmount,
          convFee: 0,
          total: totalAmount,
        },
      });

      const container = document.createElement("div");
      container.innerHTML = html;
      container.style.position = "fixed";
      container.style.left = "-9999px";
      container.style.top = "0";
      container.style.width = "210mm";
      container.style.background = "#f5f9ff";
      document.body.appendChild(container);
      const el = container.querySelector(".Receipt-card") || container.firstElementChild;
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false, backgroundColor: "#f5f9ff" });
      document.body.removeChild(container);

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`${LOGIN_STRINGS.PDF_FILENAME_PREFIX}${data.transactionId}.pdf`);
    } catch (err) {
      console.error(LOGIN_STRINGS.PDF_GENERATION_ERROR, err);
    }
  }

  return (
    <div className="login-modal-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="login-modal-box login-receipt-modal" data-aos="zoom-in">
        <div className="login-modal-top">
          <h3>{LOGIN_STRINGS.RECEIPT_TITLE}</h3>
          {/* Close button removed - user must click Download or Create Account */}
        </div>

          <div className="login-receipt-success">
            <div className="login-receipt-icon">{LOGIN_STRINGS.RECEIPT_SUCCESS_ICON}</div>
            <p>{LOGIN_STRINGS.RECEIPT_SUCCESS_MSG}</p>
          </div>

        {(data.firstName || data.lastName || data.phone) && (
          <div className="login-receipt-billto">
            <div className="login-receipt-billto-title">{LOGIN_STRINGS.RECEIPT_BILL_TO}</div>
            <div className="login-receipt-billto-content">
              {(data.firstName || data.lastName) && (
                <div className="login-receipt-billto-name">
                  {data.firstName} {data.lastName}
                </div>
              )}
              {data.phone && (
                <div className="login-receipt-billto-phone">
                  {data.phoneCode || ""} {data.phone}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="login-receipt-details">
          <div className="login-receipt-row">
            <span>{LOGIN_STRINGS.RECEIPT_TRANSACTION_ID}</span>
            <span className="login-receipt-value">{data.transactionId}</span>
          </div>
          <div className="login-receipt-row">
            <span>{LOGIN_STRINGS.RECEIPT_DATE}</span>
            <span className="login-receipt-value">{data.date}</span>
          </div>
          <div className="login-receipt-row">
            <span>{LOGIN_STRINGS.RECEIPT_ACCOUNT_TYPE}</span>
            <span className="login-receipt-value">{roleLabel}{LOGIN_STRINGS.ROLE_BADGE_MEMBERSHIP}</span>
          </div>
          <div className="login-receipt-divider"></div>
          <div className="login-receipt-row">
            <span>{LOGIN_STRINGS.RECEIPT_BASE_PRICE}</span>
            <span>{LOGIN_STRINGS.PAYMENT_CURRENCY}{data.basePrice}</span>
          </div>
          <div className="login-receipt-row">
            <span>{LOGIN_STRINGS.RECEIPT_GST}</span>
            <span>{LOGIN_STRINGS.PAYMENT_CURRENCY}{data.gstAmount}</span>
          </div>
          <div className="login-receipt-row login-receipt-total">
            <span>{LOGIN_STRINGS.RECEIPT_TOTAL_PAID}</span>
            <span>{LOGIN_STRINGS.PAYMENT_CURRENCY}{data.totalAmount}</span>
          </div>
          <div className="login-receipt-row">
            <span>{LOGIN_STRINGS.RECEIPT_PAYMENT_METHOD}</span>
            <span className="login-receipt-value">{data.paymentMethod}</span>
          </div>
          <div className="login-receipt-row">
            <span>{LOGIN_STRINGS.RECEIPT_STATUS}</span>
            <span className="login-receipt-status">{LOGIN_STRINGS.RECEIPT_STATUS_PAID}</span>
          </div>
        </div>

        <div className="login-receipt-actions">
          <button className="login-btn login-btn-outline" onClick={handleDownloadPDF}>
            {LOGIN_STRINGS.RECEIPT_DOWNLOAD_BTN}
          </button>
          <button className="login-btn login-btn-primary" onClick={onCreateAccount}>
            {LOGIN_STRINGS.RECEIPT_CREATE_ACCOUNT_BTN}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Login Page ───────────────────────────
export default function Login({ isModal = false }) {
  const navigate = useNavigate();
  const [modal, setModal] = useState(null); // { type, role }
  const [paymentModal, setPaymentModal] = useState(null); // { role }
  const [receiptData, setReceiptData] = useState(null); // { role, paymentMethod, amount }
  const handleCloseModal = useCallback(() => setModal(null), []);

  function handleAction(type, role) {
    if (type === "membership") {
      setPaymentModal({ role });
      return;
    }
    setModal({ type, role });
  }

  function handlePaymentSuccess(result) {
    const role = paymentModal?.role;
    setPaymentModal(null);

    let paymentMethodWithDetails, firstName = "", lastName = "", phoneCode = "+91", phone = "";
    if (typeof result === 'string') {
      paymentMethodWithDetails = result;
    } else {
      paymentMethodWithDetails = result.methodDisplay;
      firstName = result.firstName || "";
      lastName = result.lastName || "";
      phoneCode = result.phoneCode || "+91";
      phone = result.phone || "";
    }

    // Parse payment method and details
    let methodName = paymentMethodWithDetails;
    let methodDetails = '';

    if (paymentMethodWithDetails?.includes('|')) {
      const parts = paymentMethodWithDetails.split('|');
      methodName = parts[0];
      methodDetails = parts[1] || '';
    }

    const displayMethod = LOGIN_STRINGS.PAYMENT_METHOD_LABELS[methodName] || methodName;
    const fullMethodDisplay = methodDetails ? `${displayMethod} (${methodDetails})` : displayMethod;

    setReceiptData({
      role: role || "member",
      paymentMethod: fullMethodDisplay,
      paymentMethodRaw: methodName,
      paymentDetails: methodDetails,
      basePrice: MEMBERSHIP_BASE_PRICE,
      gstAmount: MEMBERSHIP_GST_AMOUNT,
      totalAmount: MEMBERSHIP_TOTAL_PRICE,
      transactionId: LOGIN_STRINGS.TXN_PREFIX + Date.now(),
      date: new Date().toLocaleString(),
      firstName,
      lastName,
      phoneCode,
      phone,
    });
  }

  function handleCreateAccount() {
    const role = receiptData?.role || "member";
    setReceiptData(null);
    setModal({ type: "signup", role: role, skipOtp: true });
  }

function handleSuccess(user) {
  setModal(null);
  setPaymentModal(null);
  setReceiptData(null);
  navigate('/profile');
}

  // Compact modal mode - just render the hero and user type cards without page wrapper
  if (isModal) {
    return (
      <div style={{ padding: "20px" }}>
        {/* Hero */}
        <section className="login-hero" data-aos="fade-up" style={{ padding: "20px 16px" }}>
          <h1 style={{ fontSize: "28px", whiteSpace: "normal" }}>{LOGIN_STRINGS.HERO_TITLE}</h1>
          <p>{LOGIN_STRINGS.HERO_SUBTITLE}</p>
        </section>

        {/* User Type Cards */}
        <section className="login-user-types-grid" style={{ maxWidth: "900px", width: "100%", padding: "0 0 20px" }}>
          {USER_TYPES.map((ut) => (
            <UserTypeCard key={ut.key} data={ut} onAction={handleAction} />
          ))}
        </section>

        {/* Auth Modal */}
        {modal && (
          <AuthModal
            type={modal.type}
            role={modal.role}
            onClose={handleCloseModal}
            onSuccess={handleSuccess}
            skipOtp={modal.skipOtp || false}
            setPaymentModal={setPaymentModal}
          />
        )}

        {/* Payment Modal */}
        {paymentModal && (
          <MembershipPaymentModal
            role={paymentModal.role}
            onClose={() => setPaymentModal(null)}
            onSuccess={handlePaymentSuccess}
          />
        )}

        {/* Receipt Modal */}
        {receiptData && (
          <ReceiptModal
            data={receiptData}
            onCreateAccount={handleCreateAccount}
            onClose={() => setReceiptData(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="login-page">
      {/* Hero */}
      <section className="login-hero" data-aos="fade-up">
        <h1>{LOGIN_STRINGS.HERO_TITLE}</h1>
        <p>{LOGIN_STRINGS.HERO_SUBTITLE}</p>
      </section>

      {/* User Type Cards */}
      <section className="login-user-types-grid">
        {USER_TYPES.map((ut) => (
          <UserTypeCard key={ut.key} data={ut} onAction={handleAction} />
        ))}
      </section>

      {/* Auth Modal */}
      {modal && (
        <AuthModal
          type={modal.type}
          role={modal.role}
          onClose={handleCloseModal}
          onSuccess={handleSuccess}
          skipOtp={modal.skipOtp || false}
          setPaymentModal={setPaymentModal}
        />
      )}

      {/* Payment Modal */}
      {paymentModal && (
        <MembershipPaymentModal
          role={paymentModal.role}
          onClose={() => setPaymentModal(null)}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Receipt Modal */}
      {receiptData && (
        <ReceiptModal
          data={receiptData}
          onCreateAccount={handleCreateAccount}
          onClose={() => setReceiptData(null)}
        />
      )}
    </div>
  );
}

// ── User Type Card ────────────────────────────
function UserTypeCard({ data, onAction }) {
  const isMember = data.key === "member";

  function handleMembership() {
    onAction("membership", data.key);
  }

  return (
    <div className="login-user-type-card" >
      <div className="login-card-header">
        <div className="login-card-label">{data.categoryLabel}</div>
        <h2>{data.label}</h2>
        <p className="login-card-subtitle">{data.subtitle}</p>
      </div>

      <div className="login-card-divider" />

      <div className="login-feature-list">
        {data.features.map((f, i) => (
          <FeatureItem key={i} text={f} />
        ))}
      </div>

      <div className="login-card-actions">
        <button
          className="login-btn login-btn-primary"
          onClick={() => onAction("login", data.key)}
        >
          {LOGIN_STRINGS.CARD_LOGIN_AS}
        </button>
        {!isMember && (
          <button
            className="login-btn login-btn-outline"
            onClick={() => onAction("signup", data.key)}
          >
            {LOGIN_STRINGS.CARD_SIGN_UP}
          </button>
        )}
        {isMember && (
          <button className="login-btn login-btn-membership" onClick={handleMembership}>
            {LOGIN_STRINGS.CARD_BUY}
          </button>
        )}
      </div>
    </div>
  );
}

